import { FC } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar, VariantType } from "notistack";
import LoggingAPI from "@/api/logging";

import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import { DefaultAcquisitionForm, AcquisitionFormData } from "./DefaultAcqForm";
import { Acquisition } from "@/types";
import { createAcquisition } from "@/api/acquisitions";
import { db_timestamp } from "@/utils/date-format";
import { processCustomAttributesForAPI } from "./utils/customAttributeUtils";
import { FormDataWithCustomAttributes } from "../acquisitions.types";

interface CreateAcquisitionFormProps {
  width?: number | undefined;
  onSubmit: () => void;
  onCancel: () => void;
  copyFromData?: Acquisition;
  caseInfo?: { case_id: number };
  evidenceInfo?: { evidence_id: number; case_id?: number };
}

export const CreateAcquisitionForm: FC<CreateAcquisitionFormProps> = ({
  width,
  onSubmit,
  onCancel,
  copyFromData,
  caseInfo,
  evidenceInfo,
}) => {
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const getCopySourceData = (): AcquisitionFormData | undefined => {
    if (!copyFromData) return undefined;

    // Remove unique identifiers that shouldn't be copied
    const { acq_id, uuid, ...copyableData } = copyFromData;

    return {
      ...copyableData,
      acq_name: `Copy of ${copyFromData.acq_name}`,
      acq_tool: copyFromData.acq_tool,
      storage_id: copyFromData.storage_id
        ? copyFromData.storage_id.toString()
        : undefined,
    };
  };

  const handleSubmit = async (data: AcquisitionFormData) => {
    try {
      // Convert storage_items to proper format for API
      let storageItems = null;
      if (data.storage_items && data.storage_items.length > 0) {
        storageItems = data.storage_items.map((id) =>
          typeof id === "string" ? parseInt(id) : id
        );
      }

      const formattedData: FormDataWithCustomAttributes = {
        ...data,
        evidence_id: data.evidence_id || evidenceInfo?.evidence_id || null,
        case_id: caseInfo?.case_id || evidenceInfo?.case_id || null,
        acquire_date: data.acquire_date
          ? db_timestamp(data.acquire_date)
          : null,
        contact_id: data.person_id || null,
        storage_items: storageItems,
      };

      // Remove the old storage_id field since we're using storage_items
      delete formattedData.storage_id;

      const customAttributes = processCustomAttributesForAPI(formattedData);

      if (customAttributes.length > 0) {
        formattedData.custom_attributes = customAttributes;
      }

      await createAcquisition(formattedData);

      onSubmit?.();
      enqueueSnackbar(`Acquisition ${data?.acq_name || ""} created`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Created Acquisition: ${data?.acq_name}`,
        event: "acquisition:create",
        object_type: "acquisition",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this acquisition. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle>
        {copyFromData ? "Copy Acquisition" : "Create Acquisition"}
      </FormTitle>
      <DefaultAcquisitionForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        DefaultFormData={{
          acquire_date: new Date().toISOString(),
          acquired_by_id: currentUser.user_id,
          ...getCopySourceData(),
        }}
        buttonProps={{
          submitText: copyFromData ? "Copy Acquisition" : "Add Acquisition",
        }}
        showEvidenceSelector={!!!evidenceInfo}
        caseId={caseInfo?.case_id || evidenceInfo?.case_id}
      />
    </FormWrapper>
  );
};
